import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  Computer as ComputerIcon,
  Engineering as EngineeringIcon,
  Architecture as ArchitectureIcon,
  MenuBook as HumanitiesIcon,
  Gavel as LawIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const CollegeCarousel = ({ universityId, onCollegeClick }) => {
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [, setScrollPos] = useState(0);
  const isBoostingRef = useRef(false);

  // College data with icons and colors (Prep Year first, then colleges)
  const colleges = [
    {
      code: 'PYP',
      name: t('Preparatory Year Program', 'برنامج السنة التحضيرية'),
      shortName: t('Prep Year', 'السنة التحضيرية'),
      icon: SchoolIcon,
      color: '#F57C00',
      bgColor: 'rgba(245, 124, 0, 0.08)',
      isDepartment: true,
      departmentId: 'dept-psu-CHS-pyp',
    },
    {
      code: 'CBA',
      name: t('College of Business Administration', 'كلية إدارة الأعمال'),
      shortName: t('Business', 'الأعمال'),
      icon: BusinessIcon,
      color: '#2E7D32',
      bgColor: 'rgba(46, 125, 50, 0.08)',
    },
    {
      code: 'CCIS',
      name: t('College of Computer & Information Sciences', 'كلية علوم الحاسب والمعلومات'),
      shortName: t('Computer Science', 'الحاسب'),
      icon: ComputerIcon,
      color: '#1565C0',
      bgColor: 'rgba(21, 101, 192, 0.08)',
    },
    {
      code: 'CE',
      name: t('College of Engineering', 'كلية الهندسة'),
      shortName: t('Engineering', 'الهندسة'),
      icon: EngineeringIcon,
      color: '#E65100',
      bgColor: 'rgba(230, 81, 0, 0.08)',
    },
    {
      code: 'CAD',
      name: t('College of Architecture & Design', 'كلية الهندسة المعمارية والتصميم'),
      shortName: t('Architecture', 'العمارة'),
      icon: ArchitectureIcon,
      color: '#7B1FA2',
      bgColor: 'rgba(123, 31, 162, 0.08)',
    },
    {
      code: 'CHS',
      name: t('College of Humanities & Sciences', 'كلية العلوم الإنسانية والعلوم'),
      shortName: t('Humanities', 'الإنسانية'),
      icon: HumanitiesIcon,
      color: '#00695C',
      bgColor: 'rgba(0, 105, 92, 0.08)',
    },
    {
      code: 'CL',
      name: t('College of Law', 'كلية القانون'),
      shortName: t('Law', 'القانون'),
      icon: LawIcon,
      color: '#AD1457',
      bgColor: 'rgba(173, 20, 87, 0.08)',
    },
  ];

  // Double the colleges array for seamless looping
  const infiniteColleges = [...colleges, ...colleges];
  
  // Card width (260) + Gap (16/theme.spacing(2))
  const SCROLL_AMOUNT = 276; 

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const currentScroll = container.scrollLeft;
      
      // Calculate next snap point (no RTL adjustment - scrollLeft works the same way)
      const targetScroll = direction === 'left' 
        ? currentScroll - SCROLL_AMOUNT
        : currentScroll + SCROLL_AMOUNT;

      // Pause auto-scroll during boost
      isBoostingRef.current = true;
      
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth',
      });
      
      // Resume auto-scroll after animation completes
      setTimeout(() => {
        isBoostingRef.current = false;
        setScrollPos(container.scrollLeft);
      }, 350);
    }
  };

  useEffect(() => {
    let animationFrameId;
    const scrollSpeed = 0.5; // Pixels per frame

    const animate = () => {
      if (scrollContainerRef.current && !isBoostingRef.current) {
        setScrollPos((prev) => {
          const container = scrollContainerRef.current;
          if (!container) return prev;

          const maxScroll = container.scrollWidth / 2;
          let nextPos = prev + (isArabic ? -scrollSpeed : scrollSpeed);
          
          // Reset position for infinite loop effect
          if (!isArabic) {
            if (nextPos >= maxScroll) {
              nextPos = 0;
              // Instantly jump back to start without animation for seamless loop
              container.scrollTo({ left: 0, behavior: 'auto' });
            } else {
              container.scrollLeft = nextPos;
            }
          } else {
            if (Math.abs(nextPos) >= maxScroll) {
              nextPos = 0;
              container.scrollTo({ left: 0, behavior: 'auto' });
            } else {
              container.scrollLeft = nextPos;
            }
          }
          
          return nextPos;
        });
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isArabic]);

  const handleCollegeClick = (college) => {
    if (onCollegeClick) {
      onCollegeClick(college);
    } else if (college.isDepartment && college.departmentId) {
      // Navigate directly to department page for special items like Prep Year
      navigate(`/university/${universityId}/department/${college.departmentId}`);
    } else if (universityId) {
      navigate(`/university/${universityId}?college=${college.code}`);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', py: 2 }}>
      {/* Left Arrow */}
      <IconButton
        onClick={() => scroll('left')}
        sx={{
          position: 'absolute',
          left: { xs: -8, md: -20 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          bgcolor: 'white',
          boxShadow: 2,
          '&:hover': { bgcolor: 'grey.100' },
        }}
        size="small"
        aria-label={t('Scroll left', 'التمرير لليسار')}
      >
        <ChevronLeftIcon />
      </IconButton>

      {/* Carousel Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'hidden', // Hide scrollbar completely
          px: 2,
          py: 1,
          direction: isArabic ? 'rtl' : 'ltr',
          // Mask for fade effect on edges
          maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
        }}
      >
        {infiniteColleges.map((college, index) => {
          const IconComponent = college.icon;
          // Use index in key to handle duplicates
          return (
            <Card
              key={`${college.code}-${index}`}
              sx={{
                minWidth: 260,
                maxWidth: 260,
                flexShrink: 0,
                // borderRadius is handled by theme
                transition: 'all 0.2s ease-in-out',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: college.color,
                },
              }}
              elevation={0}
            >
              <CardActionArea
                onClick={() => handleCollegeClick(college)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: college.bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconComponent sx={{ color: college.color, fontSize: 28 }} />
                    </Box>
                    <Chip
                      label={college.code}
                      size="small"
                      sx={{
                        bgcolor: college.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      fontSize: '0.95rem',
                      color: 'text.primary',
                    }}
                  >
                    {college.shortName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                    }}
                  >
                    {college.name}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {/* Right Arrow */}
      <IconButton
        onClick={() => scroll('right')}
        sx={{
          position: 'absolute',
          right: { xs: -8, md: -20 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          bgcolor: 'white',
          boxShadow: 2,
          '&:hover': { bgcolor: 'grey.100' },
        }}
        size="small"
        aria-label={t('Scroll right', 'التمرير لليمين')}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default CollegeCarousel;

